// Single, app-wide session-refresh call — the definitive fix for the
// intermittent "Invalid token".
//
// The server ROTATES the refresh-token row on every /api/auth/refresh (it
// deletes the old Session row and creates a new one — see backend
// routes/auth.ts). So two refreshes that race on the same cookie make the
// second one's `session.delete` miss (P2025) and fail — which clears the
// session and surfaces as "Invalid token" / a bounce to /login.
//
// Historically there were TWO independent in-flight guards — one in
// AuthContext (boot refresh) and one in _apiClient (on-401 refresh). They
// could not coalesce with each other, so at boot the boot-refresh and an
// early 401-triggered refresh fired two concurrent rotations and revoked
// each other. This module is the ONE place both go through, so the cookie
// is rotated exactly once no matter how many callers ask at the same time.

import { setAccessToken } from '../contexts/accessTokenStore';

const API_BASE = process.env.REACT_APP_API_URL ?? '';
const REFRESH_PATH = '/api/auth/refresh';

let _inFlight = null;
const _userListeners = new Set();

// Subscribe to the fresh user payload delivered by each successful refresh.
// AuthContext uses this so a token rotation triggered anywhere (boot, or a
// background 401 after the 1h access token expires) keeps its user state in
// sync. Returns an unsubscribe fn.
export const onRefreshedUser = (fn) => {
  _userListeners.add(fn);
  return () => _userListeners.delete(fn);
};

// Coalesced refresh. Every concurrent caller shares the same in-flight
// promise, so exactly one POST /api/auth/refresh (one cookie rotation) runs.
// Resolves to the full `{ accessToken, user }` body; rejects (and clears the
// token) when the refresh cookie is truly gone/expired/revoked.
export function refreshSession() {
  if (_inFlight) return _inFlight;
  _inFlight = (async () => {
    const res = await fetch(`${API_BASE}${REFRESH_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      cache: 'no-store',
    });
    if (!res.ok) {
      setAccessToken(null);
      const err = new Error('Session expired');
      err.status = res.status;
      throw err;
    }
    let body = null;
    try { body = await res.json(); } catch { /* non-json */ }
    if (!body?.accessToken) {
      setAccessToken(null);
      throw new Error('Refresh response missing accessToken');
    }
    setAccessToken(body.accessToken);
    _userListeners.forEach((fn) => { try { fn(body.user); } catch { /* listener error is not fatal */ } });
    return body;
  })().finally(() => {
    _inFlight = null;
  });
  return _inFlight;
}
